// Spell slots at a glance: what a character HAS (computed) and what they have USED (read live).
//
// Owner, 2026-10-08: players sometimes fudge their slots so they can cast the spell they want, so the DM
// wants to see them. D&D Beyond's record reports the slots USED, but its `available` is 0 for everyone:
// the maximum is worked out in the page from class levels, never stored. So the maximum is computed here
// from the class levels on the sheet (the standard tables; 2014 and 2024 agree) and shown as unknown
// rather than guessed when the classes cannot be read.
import type { Character } from "./types";

/** Slots by level 1..9 for each full-caster level 1..20 (the multiclass table). */
const FULL: number[][] = [
  [2], [3], [4, 2], [4, 3], [4, 3, 2], [4, 3, 3], [4, 3, 3, 1], [4, 3, 3, 2], [4, 3, 3, 3, 1], [4, 3, 3, 3, 2],
  [4, 3, 3, 3, 2, 1], [4, 3, 3, 3, 2, 1], [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1],
  [4, 3, 3, 3, 2, 1, 1, 1], [4, 3, 3, 3, 2, 1, 1, 1, 1], [4, 3, 3, 3, 3, 1, 1, 1, 1], [4, 3, 3, 3, 3, 2, 1, 1, 1], [4, 3, 3, 3, 3, 2, 2, 1, 1],
];
/** Warlock pact magic: [slot count, slot level] by warlock level. */
const pact = (n: number): [number, number] => [n >= 17 ? 4 : n >= 11 ? 3 : n >= 2 ? 2 : 1, n >= 9 ? 5 : n >= 7 ? 4 : n >= 5 ? 3 : n >= 3 ? 2 : 1];

const FULL_CLASSES = ["bard", "cleric", "druid", "sorcerer", "wizard"];
const HALF_CLASSES = ["paladin", "ranger"];
const THIRD_SUBCLASS = /eldritch knight|arcane trickster/i;

export interface ClassLevel { name: string; level: number }
export interface SlotRow { level: number; max: number; used: number; /** what the sheet really records (may exceed max); writes start from THIS, never the clamped display */ raw: number }
export interface SlotView { rows: SlotRow[]; pact?: { count: number; level: number }; known: boolean }

/** "Variant Human · Wizard 5 / Cleric 2" -> [{Wizard,5},{Cleric,2}]. Reads only the part after the last " · ". */
export function classesOf(detail: string): ClassLevel[] {
  const tail = String(detail || "").split("·").pop() || "";
  const out: ClassLevel[] = [];
  for (const m of tail.matchAll(/([A-Za-z][A-Za-z']*(?: [A-Za-z][A-Za-z']*)*?)\s+(\d{1,2})(?=\s*(?:[/,&]|$|\s))/g)) {
    const level = Number(m[2]);
    if (level >= 1 && level <= 20) out.push({ name: m[1].trim(), level });
  }
  return out;
}

/** The standard caster level and pact slots for these classes, or null when no class says. */
export function casterLevel(classes: ClassLevel[], subclasses: string[] = []): { level: number; pact?: { count: number; level: number } } | null {
  const third = subclasses.some((s) => THIRD_SUBCLASS.test(s));
  // The multiclass rule applies when more than one class CASTS: a Fighter/Paladin is still a single-class caster.
  const casts = (c: ClassLevel) => { const n = c.name.toLowerCase(); return FULL_CLASSES.includes(n) || ((HALF_CLASSES.includes(n) || n === "artificer") && (n === "artificer" || c.level >= 2)) || (third && (n === "fighter" || n === "rogue") && c.level >= 3); };
  const multi = classes.filter(casts).length > 1;
  let level = 0, any = false, pactSlots: { count: number; level: number } | undefined;
  for (const c of classes) {
    const n = c.name.toLowerCase();
    if (FULL_CLASSES.includes(n)) { level += c.level; any = true; }
    else if (HALF_CLASSES.includes(n)) { if (c.level >= 2) level += multi ? Math.floor(c.level / 2) : Math.ceil(c.level / 2); any = true; }
    else if (n === "artificer") { level += Math.ceil(c.level / 2); any = true; }
    else if (third && (n === "fighter" || n === "rogue")) { if (c.level >= 3) level += multi ? Math.floor(c.level / 3) : Math.ceil(c.level / 3); any = true; }
    else if (n === "warlock") { const [count, lvl] = pact(c.level); pactSlots = { count, level: lvl }; any = true; }
  }
  return any ? { level: Math.min(20, level), ...(pactSlots ? { pact: pactSlots } : {}) } : null;
}

/** What to draw for this character. `known` is false when the maximum could not be worked out; then only the
 *  slots seen as used are listed, with max = used (never an invented number). */
export function slotView(c: Pick<Character, "detail"> & { subclasses?: string[]; spellSlotsUsed?: { level: number; used: number }[] }): SlotView {
  const usedBy = new Map<number, number>((c.spellSlotsUsed || []).map((s) => [Number(s.level), Number(s.used) || 0]));
  const caster = casterLevel(classesOf(c.detail), c.subclasses || []);
  if (!caster) {
    const rows = [...usedBy].filter(([, u]) => u > 0).map(([level, used]) => ({ level, max: used, used, raw: used }));
    return { rows, known: false };
  }
  const table = caster.level >= 1 ? FULL[caster.level - 1] : [];
  const rows: SlotRow[] = table.map((max, i) => ({ level: i + 1, max, used: Math.min(max, usedBy.get(i + 1) || 0), raw: usedBy.get(i + 1) || 0 }));
  // Something spent beyond what the tables allow (a magic item, a feature): show it, do not hide it.
  for (const [level, used] of usedBy) if (used > 0 && !rows.some((r) => r.level === level)) rows.push({ level, max: used, used, raw: used });
  rows.sort((a, b) => a.level - b.level);
  return { rows, ...(caster.pact ? { pact: caster.pact } : {}), known: true };
}

export const ordinal = (n: number) => `${n}${n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th"}`;
